import config from '@config'
import useAuthentication from '@hooks/useAuthentication'
import useHeightTransition from '@hooks/useHeightTransition'
import useToggleMenuVisibility from '@hooks/useToggleMenuVisibility'
import useTracking from '@hooks/useTracking'
import useUser from '@hooks/useUser'
import useUserMenuVisibility from '@hooks/useUserMenuVisibility'
import translate from '@i18n'
import {
  desktopCSS,
  regularMobileCSS,
  smallMobileCSS,
  tabletCSS,
} from '@measures/responsive'
import { FunctionComponent, useCallback, useEffect } from 'react'
import { useQueryClient } from 'react-query'
import styled, { css } from 'styled-components'
import CloseIcon from '../CloseIcon'

const {
  gamification: { iceHockeyTrophyUrl },
  publication,
} = config

const label =
  'Werfe einen Blick zurück auf deine Leistungen bei der Playoff Trophy.'

// Gamification button is delivered in the cook feed, but for the user menu
// we don't get data from cook yet. It's just "hardcoded" in the frontend.
// We will need to display this menu entry again in June 2022,
// so makes sense to just hide it for now. At some point, we will deliver
// this data in the skeleton, so we don't need to do this "manually" in the frontend anymore
const showGamificationEntry = false

const MobileStyle = css`
  top: 0;
  width: calc(100% - 104px);
  min-width: 265px;
  box-shadow: -3px 0px 3px -3px rgba(0, 0, 0, 0.5);
`

const Container = styled.div<{ isVisible: boolean }>`
  ${({
    theme: {
      colors: { white },
    },
  }) => {
    return css`
      position: absolute;
      right: 0;
      overflow: hidden;
      transition: height 0.5s ease-in-out;

      display: flex;
      flex-direction: column;

      background: ${white};
      z-index: 2;

      ${desktopCSS(css`
        top: 52px;
        width: 331px;
        box-shadow: 3px 0 3px -3px rgba(0, 0, 0, 0.25),
          0px 3px 3px rgba(0, 0, 0, 0.25), -3px 0 3px -3px rgba(0, 0, 0, 0.2);
      `)}

      ${tabletCSS(css`
        top: 52px;
        width: 384px;
        box-shadow: -3px 0px 3px -3px rgba(0, 0, 0, 0.5);
      `)}

  ${regularMobileCSS(MobileStyle)}

  ${smallMobileCSS(MobileStyle)}
    `
  }}
`

const UserMenuList = styled.ul`
  width: 100%;
  padding: 0;
  margin: 0;
`

const UserMenuItem = styled.li`
  ${({
    theme: {
      colors: {
        grey: { grey_20 },
      },
      fonts: { goodOT },
    },
  }) => {
    return css`
      flex: 1;
      padding: 8px 22px;

      cursor: pointer;
      list-style: none;
      border-bottom: 1px solid ${grey_20};
      font-family: ${goodOT};
      line-height: 20px;

      animation: fall-down 125ms;
      animation-iteration-count: 1;

      ${tabletCSS(css`
        animation: none;
      `)}

      ${desktopCSS(css`
        &:last-child {
          border-bottom: none;
        }
      `)}
    `
  }}
`

const Title = styled.span`
  font-weight: 500;
  display: block;
  line-height: 22px;
  margin: 4px 0;
  &:hover {
    text-decoration: underline;
  }
`

const StyledCloseIcon = styled(CloseIcon)`
  margin-right: 24px;
  padding-top: 14px;
`

const UserMenuContent: FunctionComponent = () => {
  const queryClient = useQueryClient()
  const user = useUser()
  const { toggleUserMenu } = useToggleMenuVisibility()
  const { visitUserAccount, logout } = useAuthentication({
    prefix: 'DIR',
  })
  const userMenuVisible = useUserMenuVisibility()

  const getIsVisible = useCallback(
    () => !!queryClient.getQueryData<boolean>('userMenuVisible'),
    [queryClient]
  )
  const { setComponentRef, setNodeHeight } = useHeightTransition({
    getIsVisible,
  })

  const trackingOnClick = useCallback(() => {
    return {
      event: 'game_navbar_interaction',
      eventCategory: 'Game',
      eventAction: 'userAccount',
      eventLabel: label,
    }
  }, [])

  const handleTrack = useTracking(trackingOnClick)

  useEffect(() => {
    setNodeHeight(userMenuVisible)
  }, [setNodeHeight, userMenuVisible])

  const gotoSkiTrophy = () => {
    handleTrack()
    window.open(iceHockeyTrophyUrl, '_blank')
  }

  if (!user) {
    return null
  }

  const { email_only } = user

  const username = email_only
    ? user.email
    : `${user.first_name} ${user.last_name}`

  return (
    <Container isVisible={userMenuVisible} ref={setComponentRef}>
      <StyledCloseIcon onClick={toggleUserMenu} />
      <UserMenuList onClick={toggleUserMenu}>
        <UserMenuItem onClick={visitUserAccount}>
          <Title>{translate('login.myAccount')}</Title>
          <span>{username}</span>
        </UserMenuItem>
        {showGamificationEntry && publication.publication !== 'romandie' && (
          <UserMenuItem onClick={gotoSkiTrophy}>
            <Title>Playoff Trophy</Title>
            <span>{label}</span>
          </UserMenuItem>
        )}
        <UserMenuItem onClick={logout}>
          <Title>{translate('login.logout')}</Title>
        </UserMenuItem>
      </UserMenuList>
    </Container>
  )
}

export default UserMenuContent
